/** live-transcriber's AssemblyAI clips: 16 kHz s16le. */
const DEFAULT_SAMPLE_RATE = 16000;

/**
 * HITL-1786: copied from MiniHITL (`src/utils/miniHitl/clipPlayer.ts`);
 * restored for the pushed clip (contract D11).
 *
 * Plays a card's clip on its own AudioContext, separate from the live guest
 * audio worklet (`asrcAudio.ts`), so a replay never disturbs or is disturbed by
 * the live stream's catch-up logic. One clip at a time: a new play stops the
 * previous one.
 *
 * Browsers only let audio start after a user gesture, so `unlock()` must run
 * from a click: `play()` unlocks it, and the card plays only on the agent's
 * Play click.
 */

export interface ClipPlayback {
  /** Local wall-clock ms when playback started. */
  startedAt: number;
}

type AudioContextFactory = () => AudioContext;

const defaultFactory: AudioContextFactory = () => {
  const Ctor =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  return new Ctor();
};

export const int16ToFloat32 = (samples: Int16Array): Float32Array => {
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    out[i] = samples[i] / 0x8000;
  }
  return out;
};

export class ClipPlayer {
  private context?: AudioContext;

  private source?: AudioBufferSourceNode;

  /**
   * Bumped by every `stop()`. `play()` awaits `unlock()`; a stop during that
   * await (the card ended: deadline, answer, order) must win, or the clip would
   * start afterwards with nothing left to stop it.
   */
  private stops = 0;

  constructor(
    private readonly createContext: AudioContextFactory = defaultFactory,
    private readonly now: () => number = () => Date.now()
  ) {}

  get isUnlocked() {
    return !!this.context && this.context.state === 'running';
  }

  /** Create/resume the context. Call from a user gesture. */
  async unlock(): Promise<boolean> {
    try {
      if (!this.context) {
        this.context = this.createContext();
      }
      if (this.context.state === 'suspended') {
        await this.context.resume();
      }
      return this.context.state === 'running';
    } catch {
      return false;
    }
  }

  stop() {
    this.stops += 1;
    this.stopSource();
  }

  private stopSource() {
    const source = this.source;
    this.source = undefined;
    if (source) {
      try {
        source.stop();
      } catch {
        // never started or already ended
      }
      source.disconnect();
    }
  }

  async play(
    samples: Int16Array,
    sampleRate: number = DEFAULT_SAMPLE_RATE,
    rate = 1
  ): Promise<ClipPlayback | null> {
    if (!samples.length) {
      return null;
    }
    const stopsAtStart = this.stops;
    // A suspended context (no user gesture yet) would "play" in silence:
    // report nothing played, so it never counts as heard.
    const context = (await this.unlock()) ? this.context : undefined;
    if (!context || this.stops !== stopsAtStart) {
      return null;
    }
    // One clip at a time. Not `stop()`: a newer play() still awaiting its
    // unlock must not be cancelled by this one starting.
    this.stopSource();
    const buffer = context.createBuffer(1, samples.length, sampleRate);
    buffer.copyToChannel(int16ToFloat32(samples), 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    source.connect(context.destination);
    this.source = source;
    source.start();
    return { startedAt: this.now() };
  }

  async close() {
    this.stop();
    const context = this.context;
    this.context = undefined;
    try {
      await context?.close();
    } catch {
      // already closed
    }
  }
}
